import axios from 'axios';
import pathBuilder from '../utils';
import { signOut as googleSignOut } from './google';
import { firebase } from './firebase';
import Msal from './msal';
import { UserEmail } from '@/models';
import * as Utils from '@/utils';
import snakecaseKeys from 'snakecase-keys';

const encodedResource = encodeURI('https://api.unrulr.com');
const encodedGrantType = encodeURI(
  'urn:ietf:params:oauth:grant-type:token-exchange'
);
const encodedSubjectTokenType = encodeURI(
  'urn:ietf:params:oauth:token-type:id_token'
);
const encodedVersion = encodeURI(process.env.VUE_APP_API_VERSION || '0.0');

const buildGrantTypeBody = (idToken: string) =>
  `grant_type=${encodedGrantType}` +
  `&resource=${encodedResource}` +
  `&subject_token=${idToken}` +
  `&subject_token_type=${encodedSubjectTokenType}` +
  `&unrulr_api_version=${encodedVersion}`;

const swapForFirebaseToken = async (thirdPartyToken: string) => {
  const response = await axios.post(
    pathBuilder.basic('auth', 'firebase-custom-token'),
    buildGrantTypeBody(thirdPartyToken)
  );

  return response.data['custom_firebase_id_token'] as string;
};

const convertTokenResponse = (data: Record<string, string>) => {
  return {
    accessToken: data['access_token'],
    refreshToken: data['refresh_token']
  };
};

const auth = {
  buildGrantTypeBody,

  requestLoginCode: async (email: string) => {
    const response = await axios.post(
      pathBuilder.basic('auth', 'login-code'),
      snakecaseKeys({
        email,
        clientApp: 'admin'
      })
    );
    return response.data;
  },

  requestSignupCode: async (email: string) => {
    const response = await axios.post(
      pathBuilder.basic('auth', 'signup-code'),
      snakecaseKeys({
        email,
        clientApp: 'admin',
        intent: 'sign-up'
      })
    );

    return response.data;
  },

  exchangeEmailCodeForToken: async (email: string, code: string) => {
    const response = await axios.post(
      pathBuilder.basic('auth', 'token'),
      snakecaseKeys({
        grantType: 'email_code',
        email,
        code
      })
    );

    return convertTokenResponse(response.data);
  },

  exchangeMagicLinkForToken: async (token: string) => {
    const response = await axios.post(
      pathBuilder.basic('auth', 'token'),
      snakecaseKeys({
        grantType: 'magic_link',
        token
      })
    );

    return convertTokenResponse(response.data);
  },

  exchangeRefreshToken: async (refreshToken: string) => {
    const response = await axios.post(
      pathBuilder.basic('auth', 'token'),
      snakecaseKeys({
        grantType: 'refresh_token',
        refreshToken
      })
    );

    return convertTokenResponse(response.data);
  },

  exchangeIdToken: async (idToken: string, source: string) => {
    const response = await axios.post(
      pathBuilder.basic('auth', 'token', `?source=${source}`),
      buildGrantTypeBody(idToken)
    );

    return convertTokenResponse(response.data);
  },

  setAccessToken: (accessToken: string) => {
    axios.defaults.headers.common['Authorization'] = `BEARER ${accessToken}`;
  },

  /**
   * Note: we use the google-identity-services library to retrieve the id token.
   *
   * We also use that same library to retrieve the access token needed to list
   * google classrooms, and the server auth code needed to sync classroom rosters
   * and assignments.
   *
   * @returns A FirebaseUser
   */
  signInWithGoogle: async (idToken: string): Promise<firebase.User | null> => {
    const userCredential = await firebase
      .auth()
      .signInWithCredential(
        firebase.auth.GoogleAuthProvider.credential(idToken)
      );

    return userCredential.user;
  },

  signInWithMicrosoft: async (): Promise<firebase.User | null> => {
    const azureIdToken = await Msal.login();

    Utils.log({ azureIdToken });

    const customIdToken = await swapForFirebaseToken(azureIdToken);

    Utils.log({ customIdToken });

    const userCredential = await firebase
      .auth()
      .signInWithCustomToken(customIdToken);

    Utils.log({ userCredential });

    return userCredential.user;
  },

  signOut: async () => {
    auth.signOutFirebase();

    return Promise.all([googleSignOut(), Msal.logout()]);
  },

  signOutFirebase: async () => {
    firebase.auth().signOut();
  },

  currentFirebaseUser: () => {
    return firebase.auth().currentUser;
  },

  userInfo: async () => {
    const response = await axios.get(pathBuilder.basic('userinfo'));

    const userInfo = {
      userId: parseInt(response.data.sub, 10),
      email: response.data.email as string,
      name: response.data.name as string,
      pictureUrl: response.data.picture_url as string,
      isSuperAdmin: response.data.is_admin as boolean,
      emails: response.data.emails as UserEmail[]
    };

    // Erm, totally side-effecting here :\
    // Maybe ok since this only connects to the self endpoint?
    pathBuilder.setSuperAdmin(userInfo.isSuperAdmin);

    return userInfo;
  },

  updateName: async (userId: number, name: string) => {
    await axios.put(pathBuilder.basic('users', `${userId}`, 'name'), { name });
  }
};

export default auth;
